import { gql } from "graphql-tag";

type PropertyContractsQueryResult<T extends { id: string }> = {
  propertyContracts: T[];
};

type SubscriptionContractPayload<T> = {
  data?: { propertyContractUpdated?: T | null } | null;
};

/**
 * Matches the union of fields used by admin contracts, PM contracts settings, and sidebar.
 * Server: `propertyContractUpdated` (any material contract change for the org).
 */
export const PROPERTY_CONTRACT_UPDATED_SUBSCRIPTION = gql`
  subscription PropertyContractUpdated {
    propertyContractUpdated {
      id
      manuallyEntered
      propertyId
      interestListId
      propertyName
      propertyLegalName
      unitCount
      propertyPhone
      propertyEmail
      propertyAddress
      propertyCityStateZip
      inDevelopment
      accountEmail
      planId
      planName
      pricePerUnit
      billingFrequency
      signerName
      signerEmail
      signerTitle
      signatureRequestId
      submissionId
      embedded
      status
      sentAt
      signedAt
      cancelledAt
      contractStartDate
      initialTermEndDate
      hasPdf
      pdfUrl
    }
  }
`;

/** Merges a subscription payload into cached `propertyContracts` (by id). */
export const mergePropertyContractUpdatedIntoList = <T extends { id: string }>(
  prev: PropertyContractsQueryResult<T>,
  subscriptionData: unknown,
): PropertyContractsQueryResult<T> => {
  const payload = subscriptionData as SubscriptionContractPayload<T> | undefined;
  const next = payload?.data?.propertyContractUpdated;
  if (!next?.id) {
    return prev;
  }
  const idx = prev.propertyContracts.findIndex((c) => c.id === next.id);
  if (idx === -1) {
    return { ...prev, propertyContracts: [next, ...prev.propertyContracts] };
  }
  const merged = prev.propertyContracts.map((c) => (
    c.id === next.id ? { ...c, ...next } : c
  ));
  return { ...prev, propertyContracts: merged };
};
